import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

function fixMarkdownDividers(text) {
  return (
    text
      // Put each divider on its own line
      .replace(/(━{5,})\s*/g, "\n$1\n\n")
      // Ensure two newlines after every divider
      .replace(/(━{5,})\n(?!\n)/g, "$1\n\n")
      // Remove excess trailing space on divider lines
      .replace(/^(━{5,})\s+$/gm, "$1")
  );
}

export default function ChatPage() {
  const getOrCreateUserId = () => {
    const stored = localStorage.getItem("user_id");
    if (stored) return parseInt(stored);
    const newId = Math.floor(Math.random() * 1e9);
    localStorage.setItem("user_id", newId);
    return newId;
  };

  const userId = getOrCreateUserId();

  const [language, setLanguage] = useState(null);
  const [chatMode, setChatMode] = useState(null);
  const [subMode, setSubMode] = useState(null);
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [gender, setGender] = useState("");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [birthLocation, setBirthLocation] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (birthLocation.length < 3) {
        setLocationSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(
          "https://api.geoapify.com/v1/geocode/autocomplete",
          {
            params: {
              text: birthLocation,
              apiKey: "5ee264b566474daeb41b8bdc5b897cb1", // replace with your key
              limit: 5,
            },
          },
        );
        const suggestions = response.data.features.map(
          (feature) => feature.properties.formatted,
        );
        setLocationSuggestions(suggestions);
      } catch (error) {
        console.error("Error fetching location suggestions", error);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300); // debounce
    return () => clearTimeout(timeoutId);
  }, [birthLocation]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const chat_modes = {
    english: [
      "bazi",
      "astrology",
      "bible",
      "buddhism",
      "aristotle",
      "ray",
      "paul",
    ],
    chinese: [
      "bazi",
      "astrology",
      "bible",
      "buddhism",
      "aristotle",
      "ray",
      "paul",
    ],
  };

  const chat_mode_labels = {
    english: {
      bazi: "☯️ BaZi: Chinese Destiny Reading",
      astrology: "✨ Astrology: Understand Your Cosmic Blueprint",
      bible: "📖 Bible: Guidance from Scripture",
      buddhism: "🪷 Buddhism: Mindful Wisdom & Clarity",
      aristotle: "🏛️ Aristotle: Explore Logic, Ethics, and the Good Life",
      ray: "📘 Ray Dalio: Principles for Navigating Reality",
      paul: "💡 Paul Graham: Ideas, Startups, and Creative Thinking",
    },
    chinese: {
      bazi: "☯️ 八字：命理分析",
      astrology: "✨ 占星：探索你的星盘密码",
      bible: "📖 圣经：从经文中获得指引",
      buddhism: "🪷 佛教：清明智慧与心灵安定",
      aristotle: "🏛️ 亚里士多德：探讨逻辑、道德与幸福人生",
      ray: "📘 瑞·达利欧：原则与现实的思维框架",
      paul: "💡 保罗·格雷厄姆：关于创意与创业的思考",
    },
  };

  const chat_mode_reply = {
    english: {
      bazi: "Welcome. Share your birth details, and I’ll help interpret your BaZi chart using the wisdom of the Five Elements 🌿 and Heavenly Stems 📜. Let's explore your path through time and fate. 🕰️",
      astrology:
        "Welcome to the stars. I use astrology to help you understand your cosmic blueprint—personality, patterns, and purpose 🌠. Ask anything, and we’ll look to the sky together 🔭🌌.",
      bible:
        "Peace be with you. I’m here to answer your questions with insights and references from the Bible 📖✝️.",
      buddhism:
        "Welcome. How may I assist you on your path? I offer guidance rooted in Buddhist teachings and mindfulness 🌄.",
      aristotle:
        "Welcome, seeker of knowledge. I am here to explore with you the principles of logic, ethics, and the good life—as Aristotle once did 📚. Ask, and we shall reason together 🧠.",
      ray: "Welcome. I’m here to help you understand reality through principles, patterns, and radical truth 🪞. Ask me about life, decision-making, or the economy—I’ll answer with clarity and data-backed thinking 📊.",
      paul: "Hey there. I'm here to think through ideas with you—startups, creativity, and how to build things that matter 🧑‍💻. Ask me anything, and let’s make something meaningful 📓.",
    },
    chinese: {
      bazi: "欢迎来到命理世界。我将根据您的出生信息，结合八字五行 🌿，帮您洞察人生方向与命运走向 🔮。",
      astrology:
        "我会根据占星学的智慧，解读您的星盘 🌌，引导您理解自己与宇宙的连接 🔭。",
      bible: "我会引用圣经经文来回答您的问题，愿主的智慧 ✝️ 与您同行 🕊️。",
      buddhism:
        "阿弥陀佛。愿你此刻安宁清明 🌄，我将在佛法的智慧中为你提供指引与启发 🧘。",
      aristotle:
        "欢迎你，求知者。让我陪你探讨逻辑、道德与幸福人生的本质 🧠，从哲学中找到实践的方向 📚。",
      ray: "欢迎。我将以原则为基础 📘，帮助你理解现实、洞察趋势 🔄，并做出明智决策 📊。",
      paul: "你好！欢迎来聊创意、创业与思考的力量 🧑‍💻。我在这里陪你探索如何将点子变成改变世界的现实 🌍。",
    },
  };

  const summaries = {
    english: {
      welcome:
        "Welcome. Share your birth details, and I’ll help interpret your BaZi chart using the wisdom of the Five Elements 🌿 and Heavenly Stems 📜. Let's explore your path through time and fate. 🕰️",
      birthInfo: "Here is your selected birth info:",
      birthDate: "📅 Birth date:",
      birthTime: "⏰ Birth time:",
      gender: "🧑‍🤝‍🧑 Gender:",
      generating: "⏳ Generating report...",
      error: "Please enter a valid birth date in the past.",
    },
    chinese: {
      welcome:
        "欢迎来到命理世界。我将根据您的出生信息，结合八字五行 🌿，帮您洞察人生方向与命运走向 🔮。",
      birthInfo: "您选择的出生资料为：",
      birthDate: "📅 出生日期：",
      birthTime: "⏰ 出生时间：",
      gender: "🧑‍🤝‍🧑 性别：",
      generating: "⏳ 正在分析...",
      error: "请输入一个合法且早于今天的出生日期。",
    },
  };

  const getMessage = (type) => summaries[language]?.[type] || "";

  const addMessage = (content) => {
    setMessages((prev) => [...prev, { role: "assistant", content }]);
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
  };

  const handleModeSelect = (mode) => {
    setChatMode(mode);
    const label = chat_mode_labels[language][mode];
    setMessages((prev) => [
      ...prev,
      {
        type: "system",
        text:
          language === "chinese"
            ? `您已选择【${label}】。`
            : `You’ve selected 【${label}】.`,
      },
    ]);

    if (mode !== "bazi") {
      const welcome = chat_mode_reply[language][mode];
      if (welcome) {
        setMessages((prev) => [...prev, { type: "assistant", text: welcome }]);
      }
    }
  };

  const handleSubModeSelect = (sub) => {
    setSubMode(sub);
    const label = subModeLabel(sub);
    setMessages((prev) => [
      ...prev,
      {
        type: "system",
        text:
          language === "chinese"
            ? `您已选择【${label}】。`
            : `You’ve selected 【${label}】.`,
      },
      { type: "assistant", text: chat_mode_reply[language]["bazi"] },
    ]);
  };

  const subModeLabel = (key) => {
    const labels = {
      english: {
        overview: "🧬 BaZi Overview Report",
        monthly: "📅 2025 Month-by-Month Luck",
        life: "📖 Life Chapter Analysis",
        dayun: "🔮 Current 10-Year Cycle",
      },
      chinese: {
        overview: "🧬 命理简析",
        monthly: "📅 2025年运势月历",
        life: "📖 人生章节解析",
        dayun: "🔮 当前大运十年分析",
      },
    };
    return labels[language][key];
  };

  const validateBirthDate = () => {
    if (!birthDate) return false;
    const selected = new Date(birthDate);
    const today = new Date();
    return selected.toString() !== "Invalid Date" && selected < today;
  };

  const handleFinalSubmit = (selectedGender) => {
    if (!validateBirthDate()) {
      setError(getMessage("error"));
      return;
    }

    setGender(selectedGender);
    setError("");

    const assistantSummary = `${getMessage("birthInfo")}\n${getMessage("birthDate")} ${birthDate}\n${getMessage("birthTime")} ${birthTime}\n${getMessage("gender")} ${selectedGender}\n📍 ${language === "english" ? "Location" : "出生地"}: ${birthLocation}`;

    setMessages((prev) => [
      ...prev,
      { type: "assistant", text: assistantSummary },
      { type: "streaming", text: getMessage("generating") },
    ]);

    callFortuneAPI({
      user_id: userId,
      chat_mode: chatMode,
      language,
      year: new Date(birthDate).getFullYear(),
      month: new Date(birthDate).getMonth() + 1,
      date: new Date(birthDate).getDate() + 1,
      hour: parseInt(birthTime.split(":")[0]),
      minute: parseInt(birthTime.split(":")[1]),
      gender_female: gender === "Female" || gender === "女",
      bazi_report_mode: subMode || null,
      location: birthLocation,
    });
  };

  const shouldShowBirthInput =
    (chatMode === "bazi" && subMode && !gender) ||
    (chatMode === "astrology" && !gender);

  const callMessageAPI = async (userText) => {
    try {
      const response = await fetch("https://api.yilabs.xyz/api/message/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          chat_mode: chatMode,
          message: userText,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error("Network response was not ok.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let latestFullMessage = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        if (!chunk.trim() || chunk.includes("[DONE]")) continue;

        const fullMessage = chunk.replace(/^data:\s*/i, "");
        const fixed = fixMarkdownDividers(fullMessage);

        console.log("LATEST FULL MESSAGE:\n", fixed);

        setMessages((prev) => {
          const withoutStreaming = prev.filter((m) => m.type !== "streaming");
          return [...withoutStreaming, { type: "streaming", text: fixed }];
        });

        latestFullMessage = fixed;
      }

      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.type !== "streaming");
        return [
          ...withoutStreaming,
          { type: "assistant", text: latestFullMessage },
        ];
      });
    } catch (error) {
      console.error("Streaming error:", error);
      setMessages((prev) => [
        ...prev.filter((m) => m.type !== "streaming"),
        { type: "assistant", text: "❌ Failed to generate response." },
      ]);
    }
  };

  const callFortuneAPI = async (payload) => {
    try {
      const response = await fetch("https://api.yilabs.xyz/api/report/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok || !response.body) {
        throw new Error("Network response was not ok.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      // Add initial streaming placeholder
      let latestFullMessage = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        if (!chunk.trim() || chunk.includes("[DONE]")) continue;

        const fullMessage = chunk.replace(/^data:\s*/i, "");
        const fixed = fixMarkdownDividers(fullMessage);

        // console.log("LATEST FULL MESSAGE:\n", fixed);

        setMessages((prev) => {
          const withoutStreaming = prev.filter((m) => m.type !== "streaming");
          return [...withoutStreaming, { type: "streaming", text: fixed }];
        });

        latestFullMessage = fixed; // Store latest to finalize later
      }

      // Finalize message: convert the "streaming" message to "assistant"
      const closingNote =
        language === "english"
          ? "📝 This report is for guidance and reflection only — your destiny is in your own hands. If you have specific fortune-related questions, feel free to ask!"
          : "📝 本报告仅供参考与启发，真正的命运始终掌握在你自己手中。如果您有具体问题，欢迎继续提问！";

      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.type !== "streaming");
        return [
          ...withoutStreaming,
          { type: "assistant", text: latestFullMessage },
          { type: "assistant", text: closingNote },
        ];
      });
    } catch (error) {
      console.error("Streaming error:", error);
      setMessages((prev) => [
        ...prev.filter((m) => m.type !== "streaming"),
        { type: "assistant", text: "❌ Failed to generate report." },
      ]);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-box">
        <header className="chat-header">Yi Chat</header>

        <main className="chat-main">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`chat-message ${
                msg.type === "user"
                  ? "user-message"
                  : msg.type === "assistant" || msg.type === "streaming"
                    ? "assistant"
                    : "system"
              }`}
            >
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                {msg.text || msg.content || ""}
              </ReactMarkdown>
            </div>
          ))}
          {!language && (
            <>
              <div className="chat-message assistant">
                🌐 Please select a language / 请选择语言：
              </div>
              <div className="menu-buttons horizontal">
                <button onClick={() => handleLanguageSelect("english")}>
                  English
                </button>
                <button onClick={() => handleLanguageSelect("chinese")}>
                  中文
                </button>
              </div>
            </>
          )}

          {language && !chatMode && (
            <div className="menu-buttons vertical">
              {chat_modes[language].map((mode) => (
                <button key={mode} onClick={() => handleModeSelect(mode)}>
                  {chat_mode_labels[language][mode]}
                </button>
              ))}
            </div>
          )}

          {chatMode === "bazi" && !subMode && (
            <div className="menu-buttons vertical">
              {["overview", "monthly", "life", "dayun"].map((item) => (
                <button key={item} onClick={() => handleSubModeSelect(item)}>
                  {subModeLabel(item)}
                </button>
              ))}
            </div>
          )}

          {shouldShowBirthInput && (
            <div className="birth-info-form">
              <label>
                📅{" "}
                {language === "english"
                  ? "Select birth date:"
                  : "选择出生日期："}
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              </label>

              <label>
                ⏰{" "}
                {language === "english"
                  ? "Select birth time (local time):"
                  : "选择出生时间："}
                <input
                  type="time"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                />
              </label>
              <label>
                📍{" "}
                {language === "english"
                  ? "Select birth location:"
                  : "选择出生地点："}
                <input
                  type="text"
                  placeholder={
                    language === "english" ? "City, Country" : "城市，国家"
                  }
                  value={birthLocation}
                  onChange={(e) => setBirthLocation(e.target.value)}
                  autoComplete="off"
                />
                {locationSuggestions.length > 0 && (
                  <ul className="location-suggestions">
                    {locationSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        onClick={() => {
                          setBirthLocation(suggestion);
                          setLocationSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </label>
              <label>
                🧑‍🤝‍🧑 {language === "english" ? "Select gender:" : "选择性别："}
              </label>
              <div className="menu-buttons horizontal">
                <button
                  onClick={() =>
                    handleFinalSubmit(language === "english" ? "Male" : "男")
                  }
                >
                  {language === "english" ? "Male" : "男"}
                </button>
                <button
                  onClick={() =>
                    handleFinalSubmit(language === "english" ? "Female" : "女")
                  }
                >
                  {language === "english" ? "Female" : "女"}
                </button>
              </div>
              {error && (
                <div className="chat-message assistant error">{error}</div>
              )}
            </div>
          )}

          <div ref={chatEndRef} />
        </main>

        {((chatMode && gender) ||
          (chatMode && chatMode !== "bazi" && chatMode !== "astrology")) && (
          <form
            className="user-input-form"
            onSubmit={(e) => {
              e.preventDefault();
              const input = e.target.elements.userInput;
              const userText = input.value.trim();
              if (userText) {
                setMessages((prev) => [
                  ...prev,
                  { type: "user", text: userText },
                ]);
                input.value = "";
                // Add placeholder while streaming
                setMessages((prev) => [
                  ...prev,
                  { type: "streaming", text: "..." },
                ]);

                // Call new message streaming API
                callMessageAPI(userText);
              }
            }}
          >
            <input
              type="text"
              name="userInput"
              placeholder={
                language === "chinese"
                  ? "请输入您的问题..."
                  : "Type your question..."
              }
              className="user-input-box"
            />
            <button type="submit" className="send-button">
              ➤
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
